import type { NavigateFunction } from 'react-router-dom'
import type { Album, FileMetadata } from '../../../../types'
import { apiClient } from '../lib/api-client'
import { ApiError } from '../lib/errors'
import { getAccessToken } from '../lib/auth'
import { folderLogger } from '../lib/logger'
import type { Feedback } from './use-feedback'

export interface PhotoDownload {
  handleDownloadPhotos: () => Promise<void>
  handleDownloadAlbum: (albumId: string) => Promise<void>
}

// Whole-album and selection downloads: resolves an album's file ids from
// three sources cheapest-first (resolveAlbumFileIds), picks the sync/async
// tier and short-circuits a single photo to a direct fetch instead of a ZIP
// (startDownload), and maps the resulting error codes to the notification
// shown to the user. See the function-level comments below for the specifics
// of each.
export function usePhotoDownload(args: {
  currentFolderId: string | undefined
  albumFiles: FileMetadata[]
  albums: Album[]
  selectedPhotos: Set<string>
  clearSelection: () => void
  navigate: NavigateFunction
  feedback: Feedback
}): PhotoDownload {
  const { currentFolderId, albumFiles, albums, selectedPhotos, clearSelection, navigate, feedback } = args
  const { showNotification, showToast } = feedback

  // The per-file download loop: one fetch + one synthetic anchor click per
  // file. Used only for the single-photo short-circuit below now that the
  // `async` tier (SOL-79 phase 2) has its own real experience -- the status
  // page at /album/:id/export/:hash, not this loop.
  const downloadEachFile = async (fileIdsToDownload: string[]) => {
    if (!currentFolderId) return

    const token = await getAccessToken()
    if (!token) {
      showNotification('error', 'Authentication Error', 'Please log in to download files.')
      return
    }

    for (const fileId of fileIdsToDownload) {
      // Find file metadata to get the label for download filename
      const fileMeta = albumFiles.find(f => f.id === fileId)
      const downloadName = fileMeta?.label || fileId

      const response = await fetch(
        apiClient.getFileUrl(currentFolderId, fileId),
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (!response.ok) {
        throw new Error(`Failed to download ${downloadName}`)
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = downloadName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    const word = fileIdsToDownload.length === 1 ? 'photo' : 'photos'
    showToast(`Downloaded ${fileIdsToDownload.length} ${word}`)
    clearSelection()
  }

  /**
   * Resolve every file id in an album, for the "download the whole album"
   * entry points (SOL-175). Three sources, cheapest first:
   *
   * 1. The open album's own file list, which is already loaded.
   * 2. `album.files` from the albums listing -- handlers/albums.ts hydrates it
   *    for every album it returns.
   * 3. A fetch, for the one documented case where the listing omits `files`:
   *    when enumerating that album's keys failed, the handler returns the
   *    album WITHOUT its files rather than dropping the album (see the
   *    "Could not enumerate album files, omitting from list" branch). Treating
   *    a missing `files` as an empty album would silently download nothing.
   */
  const resolveAlbumFileIds = async (albumId: string): Promise<string[]> => {
    if (albumId === currentFolderId && albumFiles.length > 0) {
      return albumFiles.map(f => f.id)
    }
    const known = albums.find(a => a.uuid === albumId)
    if (known?.files?.length) {
      return known.files.map(f => f.id)
    }
    const full = await apiClient.getAlbum(albumId)
    return (full.files ?? []).map((f: { id: string }) => f.id)
  }

  /**
   * Start a download for an explicit set of files. Shared by the selection
   * action bar and by the whole-album entry points, so the tier gate, the
   * single-photo short-circuit and the error contract stay in ONE place --
   * an album download is not a different feature, it is the same request with
   * a different selection.
   */
  const startDownload = async (albumId: string, fileIdsToDownload: string[]) => {
    if (fileIdsToDownload.length === 0) {
      showNotification('info', 'Nothing to Download', 'This album has no photos yet.')
      return
    }

    try {
      // A single photo is not an archive. The problem this feature solves is
      // "40 photos means 40 download prompts"; one photo already means one
      // prompt, and wrapping it in AlbumName.zip so the user has to unpack a
      // JPEG is strictly worse than handing them the JPEG. The per-file path
      // also already names the download from the photo's own label.
      if (fileIdsToDownload.length === 1) {
        await downloadEachFile(fileIdsToDownload)
        return
      }

      const init = await apiClient.downloadInit(albumId, fileIdsToDownload)

      if (init.tier === 'sync') {
        // One navigation, one file. The URL carries its own signed, expiring
        // authorization, so it does not need the Bearer header a navigation
        // cannot send.
        const a = document.createElement('a')
        a.href = apiClient.toAbsolute(init.url)
        a.download = ''
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        // Pluralized like the fallback path above rather than hardcoded: the
        // single-photo short-circuit makes this branch >= 2 today, but a
        // hardcoded "photos" is a "1 photos" waiting for the day it doesn't.
        const word = fileIdsToDownload.length === 1 ? 'photo' : 'photos'
        showToast(`Preparing ${fileIdsToDownload.length} ${word}`)
        clearSelection()
        return
      }

      // Above the gate: the server started (or is already running) a
      // background export job. The status page polls it, shows progress, and
      // mints the actual download link once it's ready -- the same page a
      // "your download is ready" email links to, so starting here and
      // clicking through from the email land in the same place.
      navigate(`/album/${albumId}/export/${init.hash}`)
      clearSelection()
    } catch (error) {
      folderLogger.error('Failed to download photos', { error })
      if (error instanceof ApiError && error.isAuthError()) {
        // Distinct from the generic failure below: retrying without logging
        // back in cannot succeed, so say so instead of "try again".
        showNotification('error', 'Authentication Error', 'Please log in to download files.')
        return
      }
      // These three codes each carry a real, server-composed explanation in
      // `error.message` (an already-running export on this album, a
      // concurrency limit elsewhere, or the rolling byte budget) -- worth
      // showing verbatim rather than folding into the generic failure below.
      // None of them offer a cancel action: there is no cancel endpoint, so
      // the only honest thing to tell someone is to wait.
      if (error instanceof ApiError && (error.code === 'EXPORT_IN_PROGRESS' || error.code === 'USER_EXPORT_IN_PROGRESS')) {
        showNotification('info', 'Export Already Running', error.message)
        return
      }
      if (error instanceof ApiError && error.code === 'EXPORT_BUDGET_EXCEEDED') {
        showNotification('error', 'Download Limit Reached', error.message)
        return
      }
      showNotification('error', 'Download Failed', 'Could not download the selected photos. Please try again.')
    }
  }

  const handleDownloadPhotos = async () => {
    if (!currentFolderId || selectedPhotos.size === 0) return
    await startDownload(currentFolderId, Array.from(selectedPhotos))
  }

  /**
   * Download an entire album, from the album card's menu or from the action
   * bar (SOL-175). Deliberately NOT "select everything, then download": it
   * asks for the album as a unit, so it works from the grid without opening
   * the album, and it cannot drift out of sync with what is on screen.
   *
   * If an export for this exact selection is already running, /download/init
   * dedups on the content hash and returns that job rather than starting a
   * second one -- so this lands on the existing status page, which is the
   * intended behaviour, not a collision.
   */
  const handleDownloadAlbum = async (albumId: string) => {
    if (albumId.startsWith('temp_')) {
      // A temp album exists only in localStorage; there is nothing on the
      // server to archive. The card disables this, so reaching here means a
      // different caller -- fail with an explanation rather than a 404.
      showNotification('info', 'Not Saved Yet', 'Save this album before downloading it.')
      return
    }
    try {
      const fileIds = await resolveAlbumFileIds(albumId)
      await startDownload(albumId, fileIds)
    } catch (error) {
      folderLogger.error('Failed to resolve album files for download', { error, albumId })
      showNotification('error', 'Download Failed', 'Could not read this album. Please try again.')
    }
  }

  return { handleDownloadPhotos, handleDownloadAlbum }
}
